/**
 * PiConnectionBanner — slim, dismissable banner shown when the Pi's API
 * is unreachable (e.g. the app is opened off the owner's Tailscale
 * network, or the Pi is down).
 *
 * Polls `GET /healthz` every 20 s; the banner appears after two
 * consecutive failures (avoids flashing on a single dropped request)
 * and disappears on the next success. Toolkit features that persist in
 * the browser keep working while it is shown — this is informational.
 */
import { useEffect, useRef, useState } from 'react';

import { devApi } from '../lib/dev-api';

const POLL_MS = 20_000;
const FAILURES_BEFORE_BANNER = 2;

export function PiConnectionBanner() {
  const [unreachable, setUnreachable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const failures = useRef(0);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      const res = await devApi.healthz();
      if (cancelled) return;
      if (res.ok) {
        failures.current = 0;
        setUnreachable(false);
        setDismissed(false);
      } else {
        failures.current += 1;
        if (failures.current >= FAILURES_BEFORE_BANNER) {
          setUnreachable(true);
        }
      }
    }

    void check();
    const timer = setInterval(() => {
      void check();
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (!unreachable || dismissed) return null;

  return (
    <div
      className="flex items-center gap-3 bg-amber-500/15 border-b border-amber-500/40 px-4 py-2 text-sm text-amber-200"
      data-testid="pi-connection-banner"
      role="status"
    >
      <span aria-hidden="true">⚠️</span>
      <span className="flex-1">
        Can&apos;t reach your Pi right now — live Apex data and saved logs won&apos;t
        load. If you&apos;re away from home, check that Tailscale is running and
        signed in on this device.
      </span>
      <button
        type="button"
        className="text-amber-300/80 hover:text-amber-100 text-xs uppercase tracking-wide"
        onClick={() => setDismissed(true)}
        data-testid="pi-connection-banner-dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}
